import { supabase } from '../lib/supabase';
import type { Game, GameLineupEntry, GameFieldingEntry, MatchParticipant } from '../types';

export type {
  LineupSource,
  Game,
  GameLineupEntry,
  GameFieldingEntry,
  GamePlay,
  MatchParticipant,
  TennisMatchType,
  MatchState,
  MatchPoint,
  MatchPointSide,
  ServeOutcome,
  PointEndReason,
  ShotType,
  ErrorSpot,
  RallyBucket,
} from '../types';

/** Derive W / L / T from a game's scores. Returns null when score not yet set. */
export function gameResult(game: Pick<Game, 'our_score' | 'opponent_score'>): 'W' | 'L' | 'T' | null {
  if (game.our_score == null || game.opponent_score == null) return null;
  if (game.our_score > game.opponent_score) return 'W';
  if (game.our_score < game.opponent_score) return 'L';
  return 'T';
}

function isMissingTableError(e: any): boolean {
  // PostgREST error when table isn't in schema cache / doesn't exist
  return e?.code === 'PGRST205' || e?.status === 404;
}

/**
 * Service for managing games
 */
class GameService {
  /**
   * Get all games for a team
   */
  async getGames(teamId: string): Promise<Game[]> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('team_id', teamId)
      .order('date', { ascending: false });

    if (error) {
      console.error('[GameService] Error fetching games:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Get a single game by ID
   */
  async getGame(gameId: string): Promise<Game | null> {
    const { data, error } = await supabase
      .from('games')
      .select('*')
      .eq('id', gameId)
      .single();

    if (error) {
      console.error('[GameService] Error fetching game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Create a new game
   */
  async createGame(game: Omit<Game, 'id' | 'created_at' | 'updated_at'>): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .insert({
        team_id: game.team_id,
        date: game.date,
        opponent_name: game.opponent_name,
        home_away: game.home_away,
        location: game.location || null,
        game_time: game.game_time || null,
        season: game.season || null,
        lineup_source: game.lineup_source || 'depth_chart',
        match_format: game.match_format ?? null,
        match_type: game.match_type ?? null,
        ladder_slot: game.ladder_slot || null,
        court: game.court || null,
        opponent_school: game.opponent_school || null,
        match_state: game.match_state ?? null,
      })
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error creating game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get the players on our side of a Tennis match (1 for singles, 2 for doubles/mixed).
   */
  async getMatchParticipants(gameId: string): Promise<MatchParticipant[]> {
    const { data, error } = await supabase
      .from('match_participants')
      .select('*')
      .eq('game_id', gameId)
      .order('slot', { ascending: true });

    if (error) {
      console.error('[GameService] Error fetching match participants:', error);
      throw error;
    }
    return data || [];
  }

  /**
   * Set the players on our side of a Tennis match (replaces existing).
   */
  async setMatchParticipants(gameId: string, playerIds: string[]): Promise<void> {
    const { error: deleteError } = await supabase.from('match_participants').delete().eq('game_id', gameId);
    if (deleteError) {
      console.error('[GameService] Error clearing match participants:', deleteError);
      throw deleteError;
    }
    if (playerIds.length === 0) return;
    const rows = playerIds.slice(0, 2).map((playerId, index) => ({
      game_id: gameId,
      player_id: playerId,
      slot: index + 1,
    }));
    const { error: insertError } = await supabase.from('match_participants').insert(rows);
    if (insertError) {
      console.error('[GameService] Error saving match participants:', insertError);
      throw insertError;
    }
  }

  /**
   * Start a Tennis match: records the start time (UTC) and the scorer's IANA
   * time zone, and moves match_state to 'live'. Elapsed time is always derived
   * from started_at, never stored.
   */
  async startMatch(gameId: string): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update({
        started_at: new Date().toISOString(),
        start_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        match_state: 'live',
      })
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error starting match:', error);
      throw error;
    }
    return data;
  }

  /**
   * Complete a Tennis match: writes ended_at + match_state, and records sets
   * won into our_score / opponent_score so the games list W/L badge works
   * untouched (sc-338). `matchState` is 'abandoned' for a match ended early
   * (rain, retirement, default) rather than played to a natural finish.
   */
  async completeMatch(
    gameId: string,
    ourSetsWon: number,
    opponentSetsWon: number,
    matchState: 'complete' | 'abandoned' = 'complete'
  ): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update({
        ended_at: new Date().toISOString(),
        match_state: matchState,
        our_score: ourSetsWon,
        opponent_score: opponentSetsWon,
      })
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error completing match:', error);
      throw error;
    }
    return data;
  }

  /**
   * Reopen a match whose winning point was undone: clears ended_at/match_state
   * back to 'live' and the sets-won score written by completeMatch, so the
   * games list doesn't show a result that's no longer accurate mid-undo.
   */
  async reopenMatch(gameId: string): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update({ ended_at: null, match_state: 'live', our_score: null, opponent_score: null })
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error reopening match:', error);
      throw error;
    }
    return data;
  }

  /**
   * Set the Play Tracker tally for a personal (parent/player) account's one
   * athlete. Never negative.
   */
  async setPlayCount(gameId: string, count: number): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update({ play_count: Math.max(0, count) })
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error setting play count:', error);
      throw error;
    }
    return data;
  }

  /**
   * Update an existing game
   */
  async updateGame(
    gameId: string,
    updates: Omit<
      Partial<Omit<Game, 'id' | 'team_id' | 'created_at' | 'updated_at'>>,
      'location' | 'game_time' | 'season'
    > & {
      /** Use `null` in updates to clear optional DB columns. */
      location?: string | null;
      game_time?: string | null;
      season?: string | null;
    }
  ): Promise<Game> {
    const { data, error } = await supabase
      .from('games')
      .update(updates)
      .eq('id', gameId)
      .select()
      .single();

    if (error) {
      console.error('[GameService] Error updating game:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete a game
   */
  async deleteGame(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('games')
      .delete()
      .eq('id', gameId);

    if (error) {
      console.error('[GameService] Error deleting game:', error);
      throw error;
    }
  }

  /**
   * Get manual lineup for a game (when lineup_source === 'manual')
   */
  async getGameLineup(gameId: string): Promise<GameLineupEntry[]> {
    const { data, error } = await supabase
      .from('game_lineup')
      .select('batting_order, player_id')
      .eq('game_id', gameId)
      .order('batting_order', { ascending: true });

    if (error) {
      console.error('[GameService] Error fetching game lineup:', error);
      throw error;
    }
    return (data || []).map((row) => ({ batting_order: row.batting_order, player_id: row.player_id }));
  }

  /**
   * Set manual lineup for a game (replaces existing)
   */
  async setGameLineup(gameId: string, entries: GameLineupEntry[]): Promise<void> {
    const { error: deleteError } = await supabase.from('game_lineup').delete().eq('game_id', gameId);
    if (deleteError) {
      console.error('[GameService] Error clearing game lineup:', deleteError);
      throw deleteError;
    }
    if (entries.length === 0) return;
    const rows = entries.map((e) => ({ game_id: gameId, batting_order: e.batting_order, player_id: e.player_id }));
    const { error: insertError } = await supabase.from('game_lineup').insert(rows);
    if (insertError) {
      console.error('[GameService] Error saving game lineup:', insertError);
      throw insertError;
    }
  }

  /**
   * Get fielding assignments for a game (baseball/softball).
   */
  async getGameFielding(gameId: string): Promise<GameFieldingEntry[]> {
    const { data, error } = await supabase
      .from('game_fielding')
      .select('inning, position, player_id')
      .eq('game_id', gameId)
      .order('inning', { ascending: true })
      .order('position', { ascending: true });

    if (error) {
      if (isMissingTableError(error)) {
        // Local dev / env without migration applied yet: treat as "no saved fielding"
        return [];
      }
      console.error('[GameService] Error fetching game fielding:', error);
      throw error;
    }
    return (data || []).map((row) => ({
      inning: row.inning,
      position: row.position,
      player_id: row.player_id ?? null
    }));
  }

  /**
   * Save the final score for a game.
   * Pass null for either value to clear it.
   */
  async saveScore(
    gameId: string,
    ourScore: number | null,
    opponentScore: number | null
  ): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({ our_score: ourScore, opponent_score: opponentScore })
      .eq('id', gameId);
    if (error) {
      console.error('[GameService] Error saving game score:', error);
      throw error;
    }
  }

  /** Update only our team's score, leaving the opponent score unchanged. */
  async saveOurScore(gameId: string, ourScore: number): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({ our_score: ourScore })
      .eq('id', gameId);
    if (error) {
      console.error('[GameService] Error saving our score:', error);
      throw error;
    }
  }

  /**
   * Manually override the score (e.g. a Baseball/Softball personal account,
   * where the stats-derived score only reflects one player's own runs, not
   * the final score of the game). Sets manual_score so the stats-derived
   * auto-persist effect stops overwriting it.
   */
  async setManualScore(gameId: string, ourScore: number | null, opponentScore: number | null): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({ our_score: ourScore, opponent_score: opponentScore, manual_score: true })
      .eq('id', gameId);
    if (error) {
      console.error('[GameService] Error setting manual score:', error);
      throw error;
    }
  }

  /** Go back to a stats-derived score (Baseball/Softball) instead of a manual override. */
  async clearManualScore(gameId: string): Promise<void> {
    const { error } = await supabase
      .from('games')
      .update({ manual_score: false })
      .eq('id', gameId);
    if (error) {
      console.error('[GameService] Error clearing manual score:', error);
      throw error;
    }
  }

  /**
   * Set fielding assignments for a game (replaces existing).
   * Only rows with a player_id are persisted.
   */
  async setGameFielding(gameId: string, entries: GameFieldingEntry[]): Promise<void> {
    const { error: deleteError } = await supabase.from('game_fielding').delete().eq('game_id', gameId);
    if (deleteError) {
      if (isMissingTableError(deleteError)) {
        throw new Error(
          "Missing table 'public.game_fielding'. Apply Supabase migrations locally (e.g. `supabase db reset`) and reload."
        );
      }
      console.error('[GameService] Error clearing game fielding:', deleteError);
      throw deleteError;
    }

    const rows = entries
      .filter((e) => e.player_id)
      .map((e) => ({
        game_id: gameId,
        inning: e.inning,
        position: e.position,
        player_id: e.player_id
      }));

    if (rows.length === 0) return;
    const { error: insertError } = await supabase.from('game_fielding').insert(rows);
    if (insertError) {
      if (isMissingTableError(insertError)) {
        throw new Error(
          "Missing table 'public.game_fielding'. Apply Supabase migrations locally (e.g. `supabase db reset`) and reload."
        );
      }
      console.error('[GameService] Error saving game fielding:', insertError);
      throw insertError;
    }
  }
}

export const gameService = new GameService();

